import React, { useState } from 'react';
import { Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { resolveImageUrl } from '@/utils/image';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ServicesTab = ({ services: initialServices, onRefresh }) => {
  const [services, setServices] = useState(initialServices);
  const [newService, setNewService] = useState({ title: '', description: '' });
  const [reordering, setReordering] = useState(false);
  const [uploadingServiceId, setUploadingServiceId] = useState(null);

  React.useEffect(() => { setServices(initialServices); }, [initialServices]);

  const moveService = async (index, direction) => {
    const next = [...services];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= next.length) return;
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
    setServices(next);
    setReordering(true);
    try {
      await axios.put(`${API}/services/reorder`, { service_ids: next.map((s) => s.id) });
      toast.success('Service order updated');
    } catch {
      toast.error('Failed to reorder services');
      onRefresh();
    } finally { setReordering(false); }
  };

  const handleServiceImageUpdate = async (serviceId, file) => {
    if (!file) return;
    setUploadingServiceId(serviceId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/services/${serviceId}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Service image updated successfully');
      onRefresh();
    } catch {
      toast.error('Failed to update service image');
    } finally { setUploadingServiceId(null); }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    if (!newService.title.trim()) return;
    try {
      await axios.post(`${API}/services`, { title: newService.title.trim(), description: newService.description.trim() });
      toast.success('Service added');
      setNewService({ title: '', description: '' });
      onRefresh();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add service'); }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await axios.delete(`${API}/services/${serviceId}`);
      toast.success('Service deleted');
      onRefresh();
    } catch { toast.error('Failed to delete service'); }
  };

  return (
    <div>
      <h2 className="text-2xl font-normal mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Manage Service Sections</h2>
      <p className="text-sm text-gray-400 mb-8">Add, remove, reorder, and update background images for services on the home page.</p>

      <div className="border border-white/10 p-8 mb-10">
        <h3 className="text-lg font-normal mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Add New Service</h3>
        <form onSubmit={handleAddService} className="flex flex-col sm:flex-row gap-4" data-testid="add-service-form">
          <input type="text" value={newService.title} onChange={(e) => setNewService({ ...newService, title: e.target.value })} placeholder="Service title (e.g. Wedding Photography)" className="flex-1 bg-black border border-white/10 rounded-none px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-white/30 transition-colors" required data-testid="new-service-title-input" />
          <input type="text" value={newService.description} onChange={(e) => setNewService({ ...newService, description: e.target.value })} placeholder="Short description" className="flex-1 bg-black border border-white/10 rounded-none px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-white/30 transition-colors" data-testid="new-service-desc-input" />
          <button type="submit" className="bg-white text-black hover:bg-gray-200 rounded-none px-6 py-3 text-sm uppercase tracking-widest transition-all duration-300 shrink-0" data-testid="add-service-btn">Add</button>
        </form>
      </div>

      <div className="space-y-6">
        {services.map((service, index) => (
          <div key={service.id} className="border border-white/10 p-6 flex flex-col md:flex-row gap-6" data-testid={`service-card-${service.id}`}>
            <div className="flex flex-col items-center justify-center gap-2 md:w-12 shrink-0">
              <button onClick={() => moveService(index, 'up')} disabled={index === 0 || reordering} className="p-1 border border-white/10 hover:bg-white/10 transition-colors disabled:opacity-20 disabled:cursor-not-allowed" data-testid={`move-up-${service.id}`}><ChevronUp size={18} /></button>
              <span className="text-xs text-gray-500">{index + 1}</span>
              <button onClick={() => moveService(index, 'down')} disabled={index === services.length - 1 || reordering} className="p-1 border border-white/10 hover:bg-white/10 transition-colors disabled:opacity-20 disabled:cursor-not-allowed" data-testid={`move-down-${service.id}`}><ChevronDown size={18} /></button>
            </div>
            <div className="w-48 h-32 shrink-0 border border-white/10 bg-neutral-900 flex items-center justify-center">
              {service.image_url ? (<img src={resolveImageUrl(service.image_url)} alt={service.title} className="w-full h-full object-cover" />) : (<span className="text-xs text-gray-600 uppercase tracking-widest">No image</span>)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4 mb-2">
                <h3 className="text-xl font-normal" style={{ fontFamily: 'Playfair Display, serif' }}>{service.title}</h3>
                <button onClick={() => handleDeleteService(service.id)} className="text-gray-500 hover:text-red-400 transition-colors shrink-0 p-1" data-testid={`delete-service-${service.id}`}><Trash2 size={16} /></button>
              </div>
              <p className="text-sm text-gray-400 mb-4">{service.description}</p>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-gray-500 mb-1 block">Change Background</span>
                <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; if (f) handleServiceImageUpdate(service.id, f); }} className="w-full bg-black border border-white/10 rounded-none px-4 py-2 text-white text-sm focus:outline-none focus:border-white/30 transition-colors file:mr-4 file:py-1 file:px-3 file:rounded-none file:border-0 file:text-xs file:font-semibold file:bg-white file:text-black hover:file:bg-gray-200" disabled={uploadingServiceId === service.id} data-testid={`service-image-input-${service.id}`} />
                {uploadingServiceId === service.id && (<p className="text-sm text-gray-400 mt-2">Uploading...</p>)}
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServicesTab;
